import React, { ReactElement, useState, useRef, useEffect } from 'react';
import {
  ShowContext,
  useListContext,
  OrderDirection,
  Right,
  LoadingAnimation,
} from 'ui-builder';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { cn } from 'shadcn-ui';
import { DynamicColumnsService, GridLayoutProps, GridLayoutTheme } from '../types';
import { OrderingWrapper } from '../../OrderingWrapper';
import { useOrderingContext } from '../../OrderedList';
import { SelectForMassUpdateCheckbox } from '../../SelectForMassUpdateCheckbox';
import { EditGridColumnsButton } from './EditGridColumnsForm';
import { useGridItemsFilter } from './useGridItemsFilter';

const themeConfig = {
  [GridLayoutTheme.STANDARD]: {
    table: '',
    headerRow: 'bg-slate-50 [&_tr]:border-b [&_tr]:border-gray-200',
    headerCell: 'text-gray-500 text-app-sm font-semibold uppercase whitespace-nowrap',
    headerCellPadding: { padding: '16px 8px' },
    rowBorder: 'border-b border-gray-200/60',
    cellPadding: { padding: '16px 8px' },
    cellBorder: '',
    evenRow: '',
    minCellWidth: 'min-w-[16rem]',
    headerStickyBg: 'bg-slate-50',
  },
  [GridLayoutTheme.LIGHT]: {
    table: '',
    headerRow: '[&_tr]:border-b [&_tr]:border-gray-200',
    headerCell: 'text-gray-600 text-sm font-medium whitespace-nowrap',
    headerCellPadding: { padding: '8px 8px' },
    rowBorder: 'border-b border-gray-200',
    cellPadding: { padding: '12px 8px' },
    cellBorder: '',
    evenRow: '',
    minCellWidth: '',
    headerStickyBg: 'bg-white',
  },
  [GridLayoutTheme.TINY]: {
    table: '',
    headerRow: '[&_tr]:border-b [&_tr]:border-gray-200',
    headerCell: 'text-gray-500 text-sm font-semibold whitespace-nowrap',
    headerCellPadding: { padding: '8px 8px' },
    rowBorder: 'border-b border-gray-200/60',
    cellPadding: { padding: '8px 8px' },
    cellBorder: '',
    evenRow: '',
    minCellWidth: '',
    headerStickyBg: 'bg-white',
  },
};

function SortChevron(props: { direction: 'up' | 'down', isActive: boolean }) {
  const points = props.direction === 'up' ? '5 14 12 7 19 14' : '5 7 12 14 19 7';

  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(
        'w-[14px] h-[14px] shrink-0',
        props.isActive ? 'text-slate-700' : 'text-slate-300',
      )}
    >
      <polyline points={points} />
    </svg>
  );
}

function ShadcnSortColumnWrapper(
  props: {
    sortKey?: string,
    children: ReactElement,
  },
) {
  const listContext = useListContext();

  if (props.sortKey) {
    const isActive = listContext.orderByField === props.sortKey;
    const isDescActive = isActive && listContext.orderDirection === OrderDirection.DESC;

    const onClick = () => {
      let direction;
      if (isActive) {
        if (listContext.orderDirection === OrderDirection.ASC) {
          direction = OrderDirection.DESC;
        } else {
          direction = OrderDirection.ASC;
        }
      } else {
        direction = OrderDirection.ASC;
      }

      listContext.sort!(props.sortKey!, direction);
    };

    const chevronDirection = isDescActive ? 'up' : 'down';

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div
        className="flex items-center gap-1 w-full cursor-pointer select-none"
        onClick={onClick}
      >
        {props.children}
        <SortChevron direction={chevronDirection} isActive={isActive} />
      </div>
    );
  } else {
    return props.children;
  }
}

function ShadcnGridRow<T extends { id: number, [key: string]: any }>(props: {
  rowData: T,
  index: number,
  cells: ReactElement[],
  getWidthFunc: (i: number) => string | undefined,
  dynamicColumns?: DynamicColumnsService,
  theme: typeof themeConfig[GridLayoutTheme],
  isSticky?: boolean,
  isScrolled?: boolean,
  rowClassName?: (data: T) => string | undefined,
}) {
  const ordering = useOrderingContext();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: props.rowData.id,
    disabled: !ordering?.isEnabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const isDraggable = ordering?.isItemDraggable || (() => true);

  let draggableCell = null;

  if (ordering?.isEnabled) {
    if (isDraggable(props.rowData)) {
      draggableCell = (
        <td
          className="w-[2.5rem] h-[5rem] cursor-move relative z-[5] bg-[length:20px_20px] bg-[10%_80%] bg-no-repeat"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg version='1.1' xmlns='http://www.w3.org/2000/svg' fill='%23385b70' width='32' height='32' viewBox='0 0 32 32'%3E%3Crect x='10' y='6' width='4' height='4'/%3E%3Crect x='18' y='6' width='4' height='4'/%3E%3Crect x='10' y='14' width='4' height='4'/%3E%3Crect x='18' y='14' width='4' height='4'/%3E%3Crect x='10' y='22' width='4' height='4'/%3E%3Crect x='18' y='22' width='4' height='4'/%3E%3C/svg%3E")`,
          }}
          {...attributes}
          {...listeners}
        >
          &nbsp;
        </td>
      );
    } else {
      draggableCell = (<td />);
    }
  }

  const rowData = props.rowData as { id: number, [key: string]: any };
  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <ShowContext data={props.rowData} key={`row-${rowData.id}`}>
      <tr
        className={cn(
          props.theme.rowBorder,
          'group transition-colors hover:bg-gray-50/50',
          'last:[&>td]:border-b-0',
          props.rowClassName?.(props.rowData),
        )}
        ref={setNodeRef}
        style={style}
      >
        {draggableCell}
        {props.cells.map((child, i) => {
          const noWrap = child.props.isNoWrap || false;
          const centered = child.props.isCentered !== undefined ? child.props.isCentered : true;
          const isFirst = i === 0;

          return (
            <td
              key={`cell-${rowData.id}-${child.props.testId || child.props.label}`}
              className={cn(
                'align-middle text-gray-500',
                child.props.small ? 'w-0' : '',
                isFirst && props.isSticky && 'sticky left-0 z-[2] bg-white group-hover:bg-gray-50',
                isFirst && props.isSticky && props.isScrolled && 'after:content-[""] after:absolute after:top-0 after:right-0 after:bottom-0 after:w-px after:shadow-[4px_0_4px_rgba(0,0,0,0.1)]',
              )}
              style={{
                ...props.theme.cellPadding,
                width: props.getWidthFunc(i),
                maxWidth: props.getWidthFunc(i),
              }}
              data-testid={child.props.testId}
            >
              <div className={cn(
                'whitespace-pre-wrap break-words overflow-visible leading-[120%]',
                noWrap && 'whitespace-nowrap',
                centered && 'whitespace-nowrap text-center',
              )}>
                {child}
              </div>
            </td>
          );
        })}
        {props.dynamicColumns?.getValue()
          .filter((item) => props.dynamicColumns?.isColumnVisible(item))
          .map((item, index) => (
            <td
              className="align-middle text-gray-500"
              key={`dynamic-cell-${item}`}
              style={{
                ...props.theme.cellPadding,
                width: props.getWidthFunc(props.cells.length + index),
                maxWidth: props.getWidthFunc(props.cells.length + index),
              }}
            >
              <div className="whitespace-pre-wrap break-words overflow-visible leading-[120%]">
                {props.dynamicColumns?.getCell(item)}
              </div>
            </td>
          ))}
      </tr>
    </ShowContext>
  );
}

function ShadcnGridGroup<T extends { id: number, [key: string]: any }>(props: {
  items: T[],
  cells: ReactElement[],
  getWidthFunc: (i: number) => string | undefined,
  dynamicColumns?: DynamicColumnsService,
  theme: typeof themeConfig[GridLayoutTheme],
  isSticky?: boolean,
  isScrolled?: boolean,
  rowClassName?: (data: T) => string | undefined,
}) {
  return (
    <OrderingWrapper items={props.items}>
      <tbody>
        {props.items?.map((rowData, index) => {
          return (
            <ShadcnGridRow
              rowData={rowData}
              index={index}
              cells={props.cells}
              getWidthFunc={props.getWidthFunc}
              dynamicColumns={props.dynamicColumns}
              theme={props.theme}
              isSticky={props.isSticky}
              isScrolled={props.isScrolled}
              rowClassName={props.rowClassName}
              key={`key-${rowData.id}-${index}`}
            />
          );
        })}
      </tbody>
    </OrderingWrapper>
  );
}

export default function ShadcnGridLayoutWeb<T extends { id: number, [key: string]: any }>(
  props: GridLayoutProps,
) {
  let children = React.Children.toArray(props.children) as ReactElement[];

  const filterResult = useGridItemsFilter(children);

  children = filterResult.children;

  const listContext = useListContext<T>();

  const data = listContext.data;

  const ordering = useOrderingContext();

  const [isScrolled, setIsScrolled] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleScroll = () => {
      setIsScrolled(wrapper.scrollLeft > 0);
    };

    wrapper.addEventListener('scroll', handleScroll);
    return () => wrapper.removeEventListener('scroll', handleScroll);
  }, []);

  const getLabel = (child: ReactElement) => {
    if (child.props.label) {
      return child.props.label;
    } else {
      return child.props.source;
    }
  };

  let totalWeight = 0;

  const weights = props.weights;

  if (weights) {
    if (props.dynamicColumns) {
      for (let i = 0; i < props.dynamicColumns.getValue().length; i += 1) {
        weights.push(1.5);
      }
    }
    totalWeight = weights.reduce((prev, current) => prev + current, 0);
  }

  const getWidth = (cellNumber: number) => {
    if (weights) {
      return `${(weights[cellNumber] / totalWeight) * 100}%`;
    } else {
      return undefined;
    }
  };

  const theme = props.theme || GridLayoutTheme.STANDARD;
  const themeStyles = themeConfig[theme];

  const isSmall = (item: ReactElement) => {
    return item.props.small || item.type === SelectForMassUpdateCheckbox;
  };

  if (data?.length || !listContext.isLoading) {
    if (data?.length) {
      const getGroupFunc = ordering.getOrderGroupFunc || (() => 'default');

      const groups = data.reduce(
        (prev: Record<string, T[]>, current: T) => {
          const groupName = getGroupFunc(current);

          const result = {
            ...prev,
          };

          if (!result[groupName]) {
            result[groupName] = [];
          }

          result[groupName].push(current);

          return result;
        },
        {},
      );

      return (
        <>
          {props.dynamicColumns && props.dynamicColumns.isUpdateAllowed() && (
            <Right>
              <EditGridColumnsButton
                getValue={props.dynamicColumns.getValue}
                setValue={props.dynamicColumns.setValue}
                getTitle={props.dynamicColumns.getTitle}
                getOptionsFunc={props.dynamicColumns.getOptionsFunc}
              />
            </Right>
          )}
          {props.title}
          <div
            ref={wrapperRef}
            className={cn(
              'relative w-full overflow-x-auto scrollbar-thin',
              props.className,
            )}
          >
            <table
              className={cn(
                'w-full caption-bottom text-sm text-gray-500',
                themeStyles.table,
                listContext?.isLoading && 'opacity-50',
              )}
            >
              <thead className={themeStyles.headerRow}>
                <tr>
                  {ordering?.isEnabled && (
                    <th className="w-0 px-1">&nbsp;</th>
                  )}
                  {children.map((child) => {
                    const isCentered = child.props.isCentered !== undefined ? child.props.isCentered : true;
                    const isFirst = children.indexOf(child) === 0;

                    return (
                      <th
                        key={`header-${child.props.testId || getLabel(child)}`}
                        className={cn(
                          'text-left align-middle',
                          themeStyles.headerCell,
                          isSmall(child) ? 'w-0' : themeStyles.minCellWidth,
                          isFirst && props.isSticky && 'sticky left-0 z-[3]',
                          isFirst && props.isSticky && themeStyles.headerStickyBg,
                          isFirst && props.isSticky && isScrolled && 'after:content-[""] after:absolute after:top-0 after:right-0 after:bottom-0 after:w-px after:shadow-[4px_0_4px_rgba(0,0,0,0.1)]',
                        )}
                        style={themeStyles.headerCellPadding}
                        data-testid={child.props.testId}
                        data-test={`${child.props.source}-cell`}
                      >
                        <ShadcnSortColumnWrapper sortKey={child.props.sortName}>
                          <span className={cn(
                            'block leading-[2.5rem]',
                            isCentered && 'text-center',
                          )}>
                            {getLabel(child)}
                          </span>
                        </ShadcnSortColumnWrapper>
                      </th>
                    );
                  })}
                  {props.dynamicColumns?.getValue()
                    .filter((item) => props.dynamicColumns?.isColumnVisible(item))
                    .map((item) => (
                      <th
                        key={`header-${item}`}
                        className={cn(
                          'text-left align-middle',
                          themeStyles.headerCell,
                        )}
                        style={themeStyles.headerCellPadding}
                        data-test={`header-${item}`}
                      >
                        <ShadcnSortColumnWrapper sortKey={props.dynamicColumns!.getSortKey(item)}>
                          <span className="block leading-[2.5rem]">
                            {props.dynamicColumns?.getLabel(item)}
                          </span>
                        </ShadcnSortColumnWrapper>
                      </th>
                    ))}
                </tr>
              </thead>
              {Object.entries(groups)
                .map(([groupName, items]) => {
                  return (
                    <ShadcnGridGroup
                      items={items}
                      cells={children}
                      getWidthFunc={getWidth}
                      key={`group-${groupName}`}
                      dynamicColumns={props.dynamicColumns}
                      theme={themeStyles}
                      isSticky={props.isSticky}
                      isScrolled={isScrolled}
                      rowClassName={props.rowClassName}
                    />
                  );
                })}
            </table>
          </div>
        </>
      );
    } else {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <>{props.noDataMessage}</>;
    }
  } else {
    return (<LoadingAnimation />);
  }
}
